import React, { useEffect, useState, useCallback } from "react"
import { toast } from "react-toastify"
import { Button, Modal, Form, Row, Col, Badge, Alert } from "react-bootstrap"
import {
    getSEOSettings,
    createSEOSettings,
    getAllSEOSettings,
    deleteSEOSettings,
    activateSEOSettings
} from "../../actions/commonController"
import PaginationTable from "../../components/paginationTable"
import { utcDate, formatDate } from "../../config/utcDate"
import { isEmpty } from "../../config/funcs"

const SEOManagement = () => {
    const [seoRecords, setSeoRecords] = useState([])
    const [loading, setLoading] = useState(false)
    const [showModal, setShowModal] = useState(false)
    const [editMode, setEditMode] = useState(false)
    const [currentRecord, setCurrentRecord] = useState(null)
    const [formData, setFormData] = useState({
        websiteTitle: '',
        websiteDescription: '',
        websiteKeywords: '',
        isActive: true
    })
    const [totalItems, setTotalItems] = useState(0)
    const [page, setPage] = useState(1)
    const [quantity, setQuantity] = useState(10)
    const [initLoad, setInitLoad] = useState(false)

    useEffect(() => {
        initialization()
    }, [])

    const initialization = async () => {
        try {
            setLoading(true)
            await loadSEORecords()
        } catch (err) {
            console.error("Error in initialization function", err)
        } finally {
            setLoading(false)
        }
    }

    const loadSEORecords = useCallback(async () => {
        try {
            setLoading(true)

            // Get all SEO settings
            const seoResponse = await getAllSEOSettings()

            if (seoResponse.status === 'success') {
                let filteredData = seoResponse.data || []

                // Calculate pagination
                const total = filteredData.length
                setTotalItems(total)

                // Apply pagination
                const startIndex = (page - 1) * quantity
                const endIndex = startIndex + quantity
                const paginatedData = filteredData.slice(startIndex, endIndex)

                setSeoRecords(paginatedData)
            } else {
                toast.error('Failed to load SEO records')
            }
        } catch (error) {
            console.error('Error loading SEO records:', error)
            toast.error('Error loading SEO records')
        } finally {
            setLoading(false)
        }
    }, [page, quantity])

    const handlePageChange = (newPage) => {
        setPage(newPage)
    }

    const handleItemsPerPageChange = (newRowsPerPage) => {
        setQuantity(newRowsPerPage)
        setPage(1)
    }

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }))
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            setLoading(true)

            let response
            if (editMode && currentRecord) {
                response = await createSEOSettings({
                    ...formData,
                    id: currentRecord.id
                })
            } else {
                response = await createSEOSettings(formData)
            }

            if (response.status === 'success') {
                toast.success(editMode ? 'SEO settings updated successfully' : 'SEO settings created successfully')
                setShowModal(false)
                resetForm()
                loadSEORecords()
            } else {
                toast.error(response.error || 'Operation failed')
            }
        } catch (error) {
            console.error('Error saving SEO settings:', error)
            toast.error('Error saving SEO settings')
        } finally {
            setLoading(false)
        }
    }

    const handleEdit = (record) => {
        setCurrentRecord(record)
        setFormData({
            websiteTitle: record.websiteTitle || '',
            websiteDescription: record.websiteDescription || '',
            websiteKeywords: record.websiteKeywords || '',
            isActive: record.isActive || true
        })
        setEditMode(true)
        setShowModal(true)
    }

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete these SEO settings?')) {
            try {
                setLoading(true)
                const response = await deleteSEOSettings(id)
                if (response.status === 'success') {
                    toast.success('SEO settings deleted successfully')
                    loadSEORecords()
                } else {
                    toast.error(response.error || 'Failed to delete SEO settings')
                }
            } catch (error) {
                console.error('Error deleting SEO settings:', error)
                toast.error('Error deleting SEO settings')
            } finally {
                setLoading(false)
            }
        }
    }

    const handleToggleStatus = async (id, currentStatus) => {
        try {
            setLoading(true)
            const response = await activateSEOSettings(id)

            if (response.status === 'success') {
                toast.success(`SEO settings activated successfully`)
                loadSEORecords()
            } else {
                toast.error(response.error || 'Failed to activate settings')
            }
        } catch (error) {
            console.error('Error toggling status:', error)
            toast.error('Error updating status')
        } finally {
            setLoading(false)
        }
    }

    const resetForm = () => {
        setFormData({
            websiteTitle: '',
            websiteDescription: '',
            websiteKeywords: '',
            isActive: true
        })
        setCurrentRecord(null)
        setEditMode(false)
    }

    const openAddModal = () => {
        resetForm()
        setShowModal(true)
    }

    useEffect(() => {
        if (initLoad) loadSEORecords()
        setInitLoad(true)
    }, [page, quantity, loadSEORecords])

    // Format keywords for display
    const formatKeywords = (keywords) => {
        if (!keywords) return '-'
        const keywordArray = keywords.split(',').map(k => k.trim())
        return keywordArray.slice(0, 3).join(', ') + (keywordArray.length > 3 ? '...' : '')
    }

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="page-title-box d-flex justify-content-between align-items-center">
                        <h4 className="page-title">SEO Management</h4>
                        <div>
                            <Button variant="primary" onClick={openAddModal}>
                                <i className="ri-add-line me-1"></i> Create SEO Settings
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            {loading ? (
                                <div className="text-center">
                                    <div className="spinner-border" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-centered table-hover table-nowrap mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>ID</th>
                                                <th>Website Title</th>
                                                <th>Description</th>
                                                <th>Keywords</th>
                                                <th>Status</th>
                                                <th>Created</th>
                                                <th>Updated</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {seoRecords.map((record) => (
                                                <tr key={record.id}>
                                                    <td>{record.id}</td>
                                                    <td>
                                                        <div className="text-truncate d-block" style={{maxWidth: '200px'}}>{record.websiteTitle}</div>
                                                    </td>
                                                    <td>
                                                        <small className="text-muted text-truncate d-block" style={{ maxWidth: '100px' }}>
                                                            {record.websiteDescription ?
                                                                (record.websiteDescription.length > 80 ?
                                                                    record.websiteDescription.substring(0, 80) + '...' :
                                                                    record.websiteDescription)
                                                                : '-'}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        <small className="text-muted text-truncate d-block" style={{ maxWidth: '100px' }}>{formatKeywords(record.websiteKeywords)}</small>
                                                    </td>
                                                    <td>
                                                        <Badge bg={record.isActive ? 'success' : 'secondary'}>
                                                            {record.isActive ? 'Active' : 'Inactive'}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <small>
                                                            {!isEmpty(record.createdAt) ? formatDate(utcDate(record.createdAt)) : "-"}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        <small>
                                                            {!isEmpty(record.updatedAt) ? formatDate(utcDate(record.updatedAt)) : "-"}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex gap-1">
                                                            {!record.isActive && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline-success"
                                                                    onClick={() => handleToggleStatus(record.id, record.isActive)}
                                                                    title="Activate"
                                                                >
                                                                    <i className="ri-check-line"></i>
                                                                </Button>
                                                            )}
                                                            <Button
                                                                size="sm"
                                                                variant="outline-primary"
                                                                onClick={() => handleEdit(record)}
                                                            >
                                                                <i className="ri-edit-line"></i>
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="outline-danger"
                                                                onClick={() => handleDelete(record.id)}
                                                            >
                                                                <i className="ri-delete-bin-line"></i>
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {seoRecords.length === 0 && (
                                        <div className="text-center py-4">
                                            <p>
                                                No SEO settings found. Add your first SEO settings to get started.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <PaginationTable
                totalItems={totalItems}
                itemsPerPage={quantity}
                pageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
            />

            {/* SEO Settings Modal */}
            <Modal show={showModal} onHide={() => { setShowModal(false); resetForm() }} size="lg">
                <Modal.Header closeButton className="bg-primary text-white">
                    <Modal.Title>{editMode ? 'Edit SEO Settings' : 'Add New SEO Settings'}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Alert variant="info">
                            <strong>Note:</strong> Only one SEO setting can be active at a time. Activating new settings will deactivate the previous one.
                        </Alert>

                        <Form.Group className="mb-3">
                            <Form.Label>Website Title *</Form.Label>
                            <Form.Control
                                type="text"
                                name="websiteTitle"
                                value={formData.websiteTitle}
                                onChange={handleInputChange}
                                placeholder="e.g., My Real Estate Website - Find Your Dream Home"
                                required
                            />
                            <Form.Text className="text-muted">
                                Appears in browser tabs and search results
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Meta Description *</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="websiteDescription"
                                value={formData.websiteDescription}
                                onChange={handleInputChange}
                                placeholder="Brief description for search engines (150-160 characters recommended)"
                                required
                            />
                            <Form.Text className="text-muted">
                                {formData.websiteDescription.length} characters (150-160 recommended)
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Keywords *</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                name="websiteKeywords"
                                value={formData.websiteKeywords}
                                onChange={handleInputChange}
                                placeholder="real estate, dubai properties, luxury homes, apartments for sale, villa rental"
                                required
                            />
                            <Form.Text className="text-muted">
                                Comma-separated keywords relevant to your website
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <div className="mt-2">
                                <Form.Check
                                    type="switch"
                                    id="isActive"
                                    name="isActive"
                                    label={formData.isActive ? "Active" : "Inactive"}
                                    checked={formData.isActive}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => { setShowModal(false); resetForm() }}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? 'Saving...' : editMode ? 'Update' : 'Create'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    )
}

export default SEOManagement